/**
 * StandardCard — the ONE standardized card shell behind every handoff/
 * feedback surface in the re-synced design (queue position, "no guide
 * available" + actions, leave-a-message form, call-back form, star rating,
 * confirmation cards, and this task's ContactSummaryCard restyle). Purely
 * presentational (no state/handlers) — stays a server component so every
 * consumer isn't forced client-side just to render the shell.
 *
 * Deliberately composition-first rather than a growing prop list: `icon`/
 * `tone` cover the common tinted-circle-tile case, `tile` escapes to an
 * arbitrary node (e.g. OrbAvatar for the Sara-orb variant) when a caller
 * needs something the tone system can't express, `aside` takes any
 * right-hand node (a StandardCardPill, a StandardCardMeta, or a bespoke
 * one-off), and `children` is free-form body content. None of the follow-up
 * cards this shell is built for (queue/no-agent/leave-a-message/call-back/
 * star-rating/confirmation) should need a new prop on THIS component to
 * express their differences — they differ in what they put in these slots,
 * not in the shell itself.
 *
 * `actions` (tool-timeout-*.tsx family) is the one deliberate exception to
 * that "no new props" rule. Three call sites (handoff-timeout-options.tsx,
 * handoff-timeout-message.tsx, contact-info-card.tsx) each hand-roll the
 * identical filled-primary button recipe — that's a real, recurring shape
 * (not a one-off), so it earns a slot the same way the tone system earned
 * one. This task does NOT retrofit those three call sites onto it (out of
 * scope, and their layouts already differ enough — justify-between here,
 * flex-wrap there — that a forced migration isn't a clean win); it only
 * makes the capability exist for new consumers to opt into.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { V3_COLORS } from '@/lib/design-tokens';

export const STANDARD_CARD_TONE = {
  DEFAULT: 'default',
  WARN: 'warn',
  SUCCESS: 'success',
  /** A calm stone tile — "temporarily unavailable," not "waiting" (WARN's
   *  amber) or an error (never red). Introduced for the tool-timeout-*.tsx
   *  family (a backend tool call timing out/erroring), which needs a tone
   *  distinct from both the live-agent WARN/SUCCESS pair. */
  NEUTRAL: 'neutral',
} as const;
export type StandardCardTone =
  (typeof STANDARD_CARD_TONE)[keyof typeof STANDARD_CARD_TONE];

/** Tile bg / icon-stroke color per tone. DEFAULT and WARN deliberately share
 *  the same peach tile — only the icon stroke shifts warmer for WARN; only
 *  SUCCESS gets a fully distinct (green) tile+icon pairing. This mirrors the
 *  Figma spec exactly (not a simplification on my part) — a warn card reads
 *  as "same family, gentler nudge," not an alarm. NEUTRAL is the one other
 *  fully distinct tile+icon pairing, deliberately gray rather than warm or
 *  green/red — see STANDARD_CARD_TONE.NEUTRAL's own comment. */
const TONE_TILE_BG: Record<StandardCardTone, string> = {
  [STANDARD_CARD_TONE.DEFAULT]: V3_COLORS.standardCardTileBg,
  [STANDARD_CARD_TONE.WARN]: V3_COLORS.standardCardTileBg,
  [STANDARD_CARD_TONE.SUCCESS]: V3_COLORS.standardCardSuccessBg,
  [STANDARD_CARD_TONE.NEUTRAL]: V3_COLORS.standardCardNeutralBg,
};

const TONE_ICON_COLOR: Record<StandardCardTone, string> = {
  [STANDARD_CARD_TONE.DEFAULT]: V3_COLORS.orange,
  [STANDARD_CARD_TONE.WARN]: V3_COLORS.standardCardWarnIcon,
  [STANDARD_CARD_TONE.SUCCESS]: V3_COLORS.standardCardSuccessIcon,
  [STANDARD_CARD_TONE.NEUTRAL]: V3_COLORS.standardCardNeutralIcon,
};

/** Visual weight for a StandardCard action button. OUTLINE (default): a
 *  white/peach-border peer button, same visual weight as the filled
 *  primary — used when two options are genuinely equal choices (e.g. "Try
 *  again" vs "Leave my details"). QUIET: text-only, de-emphasized, and
 *  rendered on the OPPOSITE side of the row from primary — matches every
 *  existing "Back" affordance in this tree (handoff-timeout-message.tsx /
 *  -callback.tsx), so a quiet secondary reads as "step back," not "equal
 *  alternative." */
export const STANDARD_CARD_ACTION_VARIANT = {
  OUTLINE: 'outline',
  QUIET: 'quiet',
} as const;
export type StandardCardActionVariant =
  (typeof STANDARD_CARD_ACTION_VARIANT)[keyof typeof STANDARD_CARD_ACTION_VARIANT];

export interface StandardCardAction {
  label: ReactNode;
  onClick?: () => void;
  /** Leading icon (e.g. a refresh glyph on "Try again"). Sized/colored by
   *  the caller — this slot just positions it before the label. */
  icon?: ReactNode;
  disabled?: boolean;
  /** 'button' (default) or 'submit' — form consumers pass 'submit' so
   *  Enter-to-submit keeps working without a second handler. */
  type?: 'button' | 'submit';
  'aria-label'?: string;
}

export interface StandardCardSecondaryAction extends StandardCardAction {
  variant?: StandardCardActionVariant;
}

export interface StandardCardActionsSlot {
  primary?: StandardCardAction;
  secondary?: StandardCardSecondaryAction;
  /** Rendered as a muted line directly under the action row (e.g. a clock/
   *  mail-glyph reassurance line). Bundled into `actions` rather than a
   *  second top-level prop because every state in the design that has a
   *  footnote AND actions wants it glued immediately below the buttons —
   *  two always-adjacent slots would just be one slot with extra steps. */
  footnote?: ReactNode;
}

export interface StandardCardProps {
  /** Icon rendered inside the tone-colored circular tile — the common case.
   *  Size it yourself (~15-16px reads well inside the 30/34px tile). Ignored
   *  when `tile` is supplied. */
  icon?: ReactNode;
  /** Escape hatch that replaces the tile entirely — e.g. `<OrbAvatar
   *  size={34} variant="chat" />` for the Sara-orb tile. The node owns its
   *  own size/shape; `tone`/`icon` are ignored when this is set. */
  tile?: ReactNode;
  /** Tile/icon color treatment. Ignored when `tile` is supplied. Defaults to
   *  'default'. */
  tone?: StandardCardTone;
  /** Omit for a card with no eyebrow line (e.g. ConversationRatingCard's
   *  post-submit confirmation — "Thanks for the feedback" has no category
   *  label above it, unlike every other card this shell backs). */
  eyebrow?: ReactNode;
  title: ReactNode;
  /** Right-hand slot on the header row (vertically centered with the tile) —
   *  a <StandardCardPill>, a <StandardCardMeta>, or any custom node. Omit for
   *  no right-hand slot. */
  aside?: ReactNode;
  /** Body content below the header row. Presence alone switches the shell to
   *  the column layout with the design's 15px gap; omit for a header-row-only
   *  card (e.g. a compact confirmation). */
  children?: ReactNode;
  /** Two-weight action row (+ optional footnote), rendered below `children`.
   *  See this file's header comment for why this is the one prop that
   *  breaks from pure composition. Omitted by every existing consumer —
   *  purely additive, no layout changes when absent. */
  actions?: StandardCardActionsSlot;
  className?: string;
}

export function StandardCard({
  icon,
  tile,
  tone = STANDARD_CARD_TONE.DEFAULT,
  eyebrow,
  title,
  aside,
  children,
  actions,
  className,
}: StandardCardProps) {
  return (
    <div
      className={cn(
        // The card owns its own width. Every inline card in the transcript
        // is centred and narrower than the prose column so it reads as an
        // inset object rather than another paragraph — and because that is
        // true of EVERY StandardCard without exception, it belongs to the
        // primitive rather than to each caller. Previously each card
        // hand-rolled this lane in a wrapper, and the one that forgot
        // (conversation-rating-card) rendered full-bleed; owning it here
        // makes that class of bug unreachable. A caller that genuinely
        // needs a different width can still override via `className`,
        // which cn()/tailwind-merge resolves last.
        'mx-auto w-full max-w-[85%] sm:max-w-[70%]',
        'border-[1px] border-[#f0e7da] bg-white',
        'rounded-[15px] px-[15px] py-[14px]',
        'sm:rounded-[16px] sm:px-[20px] sm:py-[18px]',
        'shadow-[0_12px_30px_-22px_rgba(49,54,78,0.5)]',
        children || actions ? 'flex flex-col gap-[15px]' : undefined,
        className
      )}
    >
      <div className="flex items-center gap-[13px]">
        <div className="shrink-0">
          {tile ?? (
            <div
              className="flex size-[30px] items-center justify-center rounded-full sm:size-[34px]"
              style={{ backgroundColor: TONE_TILE_BG[tone] }}
            >
              <span
                className="flex items-center justify-center"
                style={{ color: TONE_ICON_COLOR[tone] }}
              >
                {icon}
              </span>
            </div>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
          {eyebrow && (
            <div
              className="text-[10.5px] font-bold uppercase tracking-[0.07em] sm:text-[11px]"
              style={{ color: V3_COLORS.standardCardEyebrow }}
            >
              {eyebrow}
            </div>
          )}
          <div
            className="text-[14px] font-semibold sm:text-[15px]"
            style={{ color: V3_COLORS.ink }}
          >
            {title}
          </div>
        </div>

        {aside && <div className="shrink-0">{aside}</div>}
      </div>

      {children && (
        <div
          className="text-[14px] leading-[1.6]"
          style={{ color: V3_COLORS.body }}
        >
          {children}
        </div>
      )}

      {actions && <StandardCardActionsRow actions={actions} />}
    </div>
  );
}

const ACTION_BASE_CLASSES =
  'inline-flex min-h-[44px] items-center justify-center gap-[6px] text-[13.5px] font-semibold disabled:cursor-not-allowed';

function StandardCardActionButton({
  action,
  variant,
}: {
  action: StandardCardAction;
  /** 'primary' is the filled recipe; anything else is a StandardCardActionVariant. */
  variant: 'primary' | StandardCardActionVariant;
}) {
  const isPrimary = variant === 'primary';
  const isQuiet = variant === STANDARD_CARD_ACTION_VARIANT.QUIET;

  return (
    <button
      type={action.type ?? 'button'}
      onClick={action.onClick}
      disabled={action.disabled}
      aria-label={action['aria-label']}
      className={cn(
        ACTION_BASE_CLASSES,
        isPrimary &&
          // The exact filled-primary recipe duplicated across
          // handoff-timeout-options.tsx / -message.tsx / contact-info-card.tsx
          // — see this file's header comment.
          'w-full rounded-[22px] bg-[#E36001] px-[18px] py-[10px] text-white shadow-[0_10px_22px_-12px_rgba(227,96,1,0.7)] disabled:bg-[#E36001]/60 sm:w-auto',
        !isPrimary &&
          !isQuiet &&
          'w-full rounded-[22px] border-[1px] border-[#F0D8BE] bg-white px-[18px] py-[10px] text-[#E36001] disabled:opacity-50 sm:w-auto',
        isQuiet && 'text-[14px] disabled:opacity-50'
      )}
      style={isQuiet ? { color: V3_COLORS.muted } : undefined}
    >
      {action.icon}
      {action.label}
    </button>
  );
}

/** Renders the two-weight action row + optional footnote. A QUIET secondary
 *  always sits on the OPPOSITE side from primary (justify-between — same
 *  spatial grammar as every hand-rolled Back/Submit row in this tree), so it
 *  has to render FIRST in DOM when quiet (CSS can't flip visual order
 *  without also flipping tab order, which would make quiet "Back" come
 *  AFTER primary "Submit" for keyboard/screen-reader users — wrong). An
 *  OUTLINE secondary is a true peer of primary, so it renders after it and
 *  the row just wraps left-aligned, matching handoff-timeout-options.tsx's
 *  existing two-peer-button layout exactly. A lone QUIET action (no
 *  primary — e.g. tool-timeout-retrying.tsx's "Skip & leave details")
 *  right-aligns since there's nothing to sit opposite. */
function StandardCardActionsRow({
  actions,
}: {
  actions: StandardCardActionsSlot;
}) {
  const { primary, secondary, footnote } = actions;
  const variant = secondary?.variant ?? STANDARD_CARD_ACTION_VARIANT.OUTLINE;
  const isQuiet = variant === STANDARD_CARD_ACTION_VARIANT.QUIET;
  const rowAlign = isQuiet
    ? primary
      ? 'sm:justify-between'
      : 'sm:justify-end'
    : 'sm:justify-start';

  return (
    <div className="flex flex-col gap-[10px]">
      <div
        className={cn(
          'flex flex-col gap-[10px] sm:flex-row sm:flex-wrap sm:items-center sm:gap-[9px]',
          rowAlign
        )}
      >
        {isQuiet && secondary && (
          <StandardCardActionButton action={secondary} variant={variant} />
        )}
        {primary && (
          <StandardCardActionButton action={primary} variant="primary" />
        )}
        {!isQuiet && secondary && (
          <StandardCardActionButton action={secondary} variant={variant} />
        )}
      </div>
      {footnote && (
        <div
          className="flex items-center gap-[6px] text-[12.5px]"
          style={{ color: V3_COLORS.standardCardMeta }}
        >
          {footnote}
        </div>
      )}
    </div>
  );
}

/** Ready-made right-hand pill (e.g. a status word) — reuses the existing
 *  amber amberBg/amberText tokens rather than new one-off literals, since
 *  those are the exact values the Figma pill spec calls for. */
export function StandardCardPill({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'rounded-[20px] px-[12px] py-[5px] text-[12px] font-semibold',
        className
      )}
      style={{ backgroundColor: V3_COLORS.amberBg, color: V3_COLORS.amberText }}
    >
      {children}
    </span>
  );
}

/** Ready-made right-hand meta node (e.g. an icon + short label) — plain text
 *  color only; layout (gap/icon) is the caller's, since the two meta use
 *  cases seen so far (a lock glyph, a plain label) don't share enough markup
 *  to warrant baking a layout in here too. */
export function StandardCardMeta({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'flex items-center gap-[4px] text-[11.5px] font-semibold',
        className
      )}
      style={{ color: V3_COLORS.standardCardMeta }}
    >
      {children}
    </span>
  );
}
